// Copyright 2026 - Simplified Lending Solutions, LLC
import { NavigationError } from '@angular/router';

/**
 * A lazy route chunk failed to download. This happens when a tab that is
 * already open keeps referencing chunk names from a previous build — after a
 * deploy, or after `ng serve` restarts — so the browser reports a 404 /
 * module fetch error and the Router cancels the navigation. Without a
 * handler the click looks dead: the URL stays where it was.
 */
export function isChunkLoadError(error: unknown): boolean {
  const message = error instanceof Error ? error.message : String(error ?? '');
  return /Failed to fetch dynamically imported module|Importing a module script failed|error loading dynamically imported module|Loading chunk \S+ failed/i.test(
    message,
  );
}

const RELOAD_KEY = 'sls.chunk-reload';
const RELOAD_WINDOW_MS = 10_000;

/**
 * Router `withNavigationErrorHandler` callback: reload the page at the target
 * URL so the browser picks up the current build. Guards against a reload
 * loop — if the same URL already triggered a reload a moment ago, the error is
 * left to surface instead (the chunk is really missing).
 */
export function chunkLoadErrorHandler(
  event: NavigationError,
  win: Pick<Window, 'location' | 'sessionStorage'> = window,
  now: () => number = Date.now,
): boolean {
  if (!isChunkLoadError(event.error)) {
    return false;
  }

  let last: { url: string; at: number } | null = null;
  try {
    last = JSON.parse(win.sessionStorage.getItem(RELOAD_KEY) ?? 'null');
  } catch {
    last = null;
  }
  if (last?.url === event.url && now() - last.at < RELOAD_WINDOW_MS) {
    return false;
  }

  try {
    win.sessionStorage.setItem(RELOAD_KEY, JSON.stringify({ url: event.url, at: now() }));
  } catch {
    // Storage unavailable (private mode) — a single reload is still the right move.
  }
  win.location.assign(event.url);
  return true;
}
